/**
 * Stato di autenticazione della dashboard.
 *
 * L'autenticazione è gestita **interamente dal gateway** (backend-for-frontend,
 * `.claude/handoff/06`): il browser manda nome utente e password a una rotta
 * del gateway, che parla con Keycloak e risponde con un cookie di sessione
 * `HttpOnly`. Da lì in poi il browser non possiede nulla di segreto — nessun
 * token in memoria, nessuno in `localStorage`, nessuno in `sessionStorage` —
 * e non può nemmeno leggere il cookie.
 *
 * Lo stato della sessione non si deduce dal client: si chiede al gateway con
 * `GET /api/v1/auth/session` al caricamento e dopo ogni azione che lo cambia.
 * Il gateway è l'unico a sapere se la sessione è ancora valida, se manca il
 * secondo fattore o se va ancora registrato.
 *
 * I codici di recupero vivono solo in memoria e solo fino a quando l'utente
 * dichiara di averli salvati: copiarli in uno storage del browser li renderebbe
 * leggibili a qualunque script della pagina, vanificando il secondo fattore.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiClient, ApiError } from '../api/client';
import { t } from '../i18n';
import {
  AuthApi,
  type EnrollmentResponse,
  type ImpersonationRequest,
  type ImpersonationState,
  type LoginMethod,
  type MfaCredential,
  type SessionStatus,
} from './authApi';
import { ANONYMOUS, toSnapshot, type AuthenticatedUser, type SessionSnapshot } from './sessionSnapshot';

export type { AuthenticatedUser } from './sessionSnapshot';

/** Stato dell'autenticazione esposto ai componenti. */
export interface AuthState {
  /** Stato della sessione secondo il gateway. */
  status: SessionStatus;
  /** True finché la prima lettura della sessione non è conclusa. */
  loading: boolean;
  /** True mentre un'azione di autenticazione è in corso. */
  busy: boolean;
  /** Utente autenticato, `null` quando la sessione non è attiva. */
  user: AuthenticatedUser | null;
  /** True se il ruolo dell'utente rende obbligatorio il secondo fattore. */
  mfaRequired: boolean;
  /** True se l'utente ha già registrato un secondo fattore. */
  mfaEnrolled: boolean;
  /** Come è nata la sessione (password o Google), `null` se non si sa. */
  loginMethod: LoginMethod | null;
  /** Messaggio dell'ultimo errore, già pronto per l'utente. */
  error: string | null;
  /** Secondi da attendere dopo un 429, se il gateway li ha dichiarati. */
  retryAfterSeconds: number | null;
  /** Dati di registrazione del secondo fattore, quando richiesti. */
  enrollment: EnrollmentResponse | null;
  /** Codici di recupero da mostrare una sola volta, `null` dopo la conferma. */
  recoveryCodes: string[] | null;
  /** Codici di recupero ancora spendibili, dopo una verifica riuscita. */
  recoveryCodesLeft: number | null;
  /**
   * Impersonazione in corso, `null` altrimenti. Quando c'è, `user` è l'utente
   * impersonato e il banner dice chi c'è davvero dietro.
   */
  impersonation: ImpersonationState | null;
  /** True se la sessione può avviare un'impersonazione (lo decide il gateway). */
  canImpersonate: boolean;
  /** Durata massima di un'impersonazione, in minuti. */
  impersonationMaxMinutes: number;
  /** True se l'avvio di un'impersonazione chiede il codice TOTP (lo decide il gateway). */
  impersonationRequiresTotp: boolean;
  /** Client HTTP condiviso, già configurato con cookie e rientro su 401. */
  client: ApiClient;
  /**
   * Apre una sessione.
   *
   * @param username Nome utente.
   * @param password Password.
   */
  login: (username: string, password: string) => Promise<void>;
  /** Chiede al gateway il QR code per registrare il secondo fattore. */
  enrollMfa: () => Promise<void>;
  /**
   * Conferma la registrazione del secondo fattore.
   *
   * @param code Codice TOTP a sei cifre.
   */
  verifyEnrollment: (code: string) => Promise<void>;
  /**
   * Supera la verifica del secondo fattore.
   *
   * @param credential Codice TOTP oppure codice di recupero.
   */
  verifyMfa: (credential: MfaCredential) => Promise<void>;
  /** Dichiara di aver salvato i codici di recupero e li dimentica. */
  acknowledgeRecoveryCodes: () => void;
  /**
   * Avvia l'impersonazione di un utente e rilegge la sessione.
   *
   * A differenza delle azioni di accesso non scrive `error`: l'errore arriva
   * al chiamante, che lo mostra nella propria finestra di conferma.
   *
   * @param request Utente, motivazione, codice e durata.
   * @throws ApiError Se il gateway rifiuta l'avvio.
   */
  startImpersonation: (request: ImpersonationRequest) => Promise<void>;
  /**
   * Torna all'account dell'amministratore e rilegge la sessione.
   *
   * @throws ApiError Se il gateway rifiuta la richiesta.
   */
  stopImpersonation: () => Promise<void>;
  /** Rilegge la sessione dal gateway, per esempio alla scadenza di un'impersonazione. */
  refreshSession: () => Promise<void>;
  /** Chiude la sessione. */
  logout: () => Promise<void>;
  /** Azzera l'errore mostrato. */
  clearError: () => void;
}

/**
 * Messaggio unico per ogni credenziale rifiutata.
 *
 * È una funzione e non una costante perché una costante sarebbe valutata al
 * caricamento del modulo e resterebbe nella lingua di allora. Il testo è
 * uniforme in ogni lingua per costruzione: è il messaggio che non distingue
 * utente inesistente da password errata.
 *
 * @returns La frase nella lingua attiva.
 */
export function invalidCredentials(): string {
  return t('auth.errors.invalidCredentials');
}

/**
 * Messaggio mostrato quando la sessione decade durante la navigazione.
 *
 * @returns La frase nella lingua attiva.
 */
export function sessionExpired(): string {
  return t('common.errors.sessionExpired');
}

const AuthContext = createContext<AuthState | null>(null);

/** Proprietà del provider. */
export interface AuthProviderProps {
  /** URL base del gateway; stringa vuota per la stessa origine. */
  baseUrl?: string;
  /** Client già costruito, usato dai test per osservare le richieste. */
  client?: ApiClient;
  /** Sottoalbero da avvolgere. */
  children: ReactNode;
}

/**
 * Traduce un errore di rete in un messaggio per l'utente.
 *
 * Il 401 diventa **sempre** lo stesso messaggio, qualunque cosa risponda il
 * gateway: utente inesistente, password errata e codice errato non devono
 * essere distinguibili, altrimenti la pagina di accesso diventa un oracolo che
 * dice quali nomi utente esistono.
 *
 * @param caught Errore intercettato.
 * @returns Messaggio e attesa suggerita.
 */
export function describeAuthError(caught: unknown): {
  message: string;
  retryAfterSeconds: number | null;
} {
  if (caught instanceof ApiError) {
    if (caught.isUnauthenticated) {
      return { message: invalidCredentials(), retryAfterSeconds: null };
    }
    if (caught.isRateLimited) {
      const wait = caught.retryAfterSeconds;
      return {
        message:
          wait === null
            ? t('auth.errors.tooManyAttempts')
            : t('auth.errors.tooManyAttemptsIn', { count: wait }),
        retryAfterSeconds: wait,
      };
    }
    return { message: caught.message, retryAfterSeconds: null };
  }
  return { message: t('common.errors.generic'), retryAfterSeconds: null };
}

/**
 * Fornisce lo stato di autenticazione all'albero dei componenti.
 *
 * @param props.baseUrl URL base del gateway.
 * @param props.client Client già costruito (usato dai test).
 * @param props.children Sottoalbero da avvolgere.
 * @returns Il provider di contesto.
 */
export function AuthProvider({ baseUrl = '', client, children }: AuthProviderProps): JSX.Element {
  const apiClient = useMemo(() => client ?? new ApiClient({ baseUrl }), [client, baseUrl]);
  const authApi = useMemo(() => new AuthApi(apiClient), [apiClient]);

  const [session, setSession] = useState<SessionSnapshot>(ANONYMOUS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [enrollment, setEnrollment] = useState<EnrollmentResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [recoveryCodesLeft, setRecoveryCodesLeft] = useState<number | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Rilegge la sessione dal gateway e la riflette nello stato.
   *
   * @returns Lo stato letto, utile a chi deve decidere il passo successivo.
   */
  const refresh = useCallback(async (): Promise<SessionSnapshot> => {
    const payload = await authApi.session();
    const snapshot = toSnapshot(payload);
    if (mounted.current) {
      setSession(snapshot);
    }
    return snapshot;
  }, [authApi]);

  // Prima lettura: lo stato iniziale non si indovina, si chiede al gateway.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await refresh();
      } catch {
        // Gateway irraggiungibile o risposta inattesa: si resta anonimi, che è
        // la posizione prudente. L'errore reale riapparirà al primo accesso.
        if (!cancelled && mounted.current) {
          setSession(ANONYMOUS);
        }
      } finally {
        if (!cancelled && mounted.current) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  /** Riporta l'applicazione allo stato anonimo, dimenticando tutto. */
  const reset = useCallback((message: string | null) => {
    setSession(ANONYMOUS);
    setEnrollment(null);
    setRecoveryCodes(null);
    setRecoveryCodesLeft(null);
    setRetryAfterSeconds(null);
    setError(message);
  }, []);

  // Vero quando il 401 in corso è arrivato da una rotta protetta, cioè è una
  // sessione decaduta e non una credenziale rifiutata. I due casi hanno lo
  // stesso codice di stato ma messaggi diversi, e quello giusto è già stato
  // impostato dal gestore qui sotto: `run` non deve sovrascriverlo.
  const sessionLost = useRef(false);

  // Un 401 su una rotta protetta significa sessione decaduta: si torna
  // all'accesso invece di ritentare in silenzio.
  useEffect(() => {
    apiClient.setUnauthenticatedHandler(() => {
      sessionLost.current = true;
      if (mounted.current) {
        reset(sessionExpired());
      }
    });
    return () => {
      apiClient.setUnauthenticatedHandler(undefined);
    };
  }, [apiClient, reset]);

  /**
   * Esegue un'azione di autenticazione gestendo errori e indicatore di attesa.
   *
   * @param action Azione da eseguire.
   */
  const run = useCallback(async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    setRetryAfterSeconds(null);
    sessionLost.current = false;
    try {
      await action();
    } catch (caught) {
      const described = describeAuthError(caught);
      // Se la sessione è decaduta, il messaggio corretto l'ha già scritto il
      // gestore del 401: sovrascriverlo con «credenziali non valide» direbbe
      // all'utente una cosa falsa.
      if (mounted.current && !sessionLost.current) {
        setError(described.message);
        setRetryAfterSeconds(described.retryAfterSeconds);
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
      }
    }
  }, []);

  const login = useCallback(
    (username: string, password: string) =>
      run(async () => {
        await authApi.login(username, password);
        // La risposta del login porta solo lo stato: nome utente e ruoli si
        // leggono dalla sessione, che è l'unica fonte di verità.
        await refresh();
      }),
    [authApi, refresh, run],
  );

  const enrollMfa = useCallback(
    () =>
      run(async () => {
        const received = await authApi.enroll();
        if (mounted.current) {
          setEnrollment(received);
        }
      }),
    [authApi, run],
  );

  const verifyEnrollment = useCallback(
    (code: string) =>
      run(async () => {
        const verified = await authApi.verifyEnrollment(code);
        if (mounted.current) {
          // I codici restano in memoria fino alla conferma dell'utente, poi
          // spariscono: non esiste copia su disco.
          setRecoveryCodes(verified.recovery_codes);
          setEnrollment(null);
        }
        await refresh();
      }),
    [authApi, refresh, run],
  );

  const verifyMfa = useCallback(
    (credential: MfaCredential) =>
      run(async () => {
        const verified = await authApi.verifyMfa(credential);
        if (mounted.current) {
          setRecoveryCodesLeft(verified.recovery_codes_left);
        }
        await refresh();
      }),
    [authApi, refresh, run],
  );

  const acknowledgeRecoveryCodes = useCallback(() => {
    setRecoveryCodes(null);
  }, []);

  const logout = useCallback(
    () =>
      run(async () => {
        try {
          await authApi.logout();
        } finally {
          // Anche se il gateway fallisce, la dashboard non deve restare a
          // mostrare dati di una sessione che l'utente ha chiesto di chiudere.
          if (mounted.current) {
            reset(null);
          }
        }
      }),
    [authApi, reset, run],
  );

  const startImpersonation = useCallback(
    async (request: ImpersonationRequest): Promise<void> => {
      await authApi.startImpersonation(request);
      await refresh();
    },
    [authApi, refresh],
  );

  const stopImpersonation = useCallback(async (): Promise<void> => {
    try {
      await authApi.stopImpersonation();
    } finally {
      // Anche se il ritorno fallisce, lo stato mostrato deve essere quello vero.
      await refresh();
    }
  }, [authApi, refresh]);

  const refreshSession = useCallback(async (): Promise<void> => {
    await refresh();
  }, [refresh]);

  const clearError = useCallback(() => {
    setError(null);
    setRetryAfterSeconds(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status: session.status,
      loading,
      busy,
      user: session.user,
      mfaRequired: session.mfaRequired,
      mfaEnrolled: session.mfaEnrolled,
      loginMethod: session.loginMethod,
      error,
      retryAfterSeconds,
      enrollment,
      recoveryCodes,
      recoveryCodesLeft,
      impersonation: session.impersonation,
      canImpersonate: session.canImpersonate,
      impersonationMaxMinutes: session.impersonationMaxMinutes,
      impersonationRequiresTotp: session.impersonationRequiresTotp,
      client: apiClient,
      login,
      enrollMfa,
      verifyEnrollment,
      verifyMfa,
      acknowledgeRecoveryCodes,
      startImpersonation,
      stopImpersonation,
      refreshSession,
      logout,
      clearError,
    }),
    [
      session,
      loading,
      busy,
      error,
      retryAfterSeconds,
      enrollment,
      recoveryCodes,
      recoveryCodesLeft,
      apiClient,
      login,
      enrollMfa,
      verifyEnrollment,
      verifyMfa,
      acknowledgeRecoveryCodes,
      startImpersonation,
      stopImpersonation,
      refreshSession,
      logout,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Accede allo stato di autenticazione.
 *
 * @returns Lo stato corrente.
 * @throws Error Se usato fuori da `AuthProvider`.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth richiede AuthProvider');
  }
  return context;
}

/**
 * Stato di autenticazione, oppure `null` fuori da `AuthProvider`.
 *
 * Per gli inviti facoltativi (verifica in due passaggi) che compaiono anche in
 * pagine provate senza provider: lì l'invito semplicemente non c'è.
 *
 * @returns Lo stato corrente o `null`.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}
